import { getAccessToken, getScopeHeaders, setScope } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_TENANT_API ?? '/api/v1';

export type TenantStatus = 'pending' | 'active' | 'suspended' | 'archived' | 'deleting';

export type TenantBranding = {
  logo_url?: string;
  favicon_url?: string;
  primary_color?: string;
  accent_color?: string;
  display_name?: string;
  theme?: string;
};

export type TenantProfile = {
  timezone?: string;
  locale?: string;
  currency?: string;
  language?: string;
  date_format?: string;
  number_format?: string;
  website?: string;
  industry?: string;
};

export type TenantBusiness = {
  legal_name?: string;
  registration_number?: string;
  gst_number?: string;
  pan?: string;
  business_type?: string;
  country?: string;
  state?: string;
  financial_year_start?: string;
};

export type TenantTax = {
  default_tax_rules?: Record<string, string>;
  marketplace_tax?: Record<string, string>;
  exemption_codes?: string[];
  reverse_charge?: boolean;
  composition_scheme?: boolean;
};

export type TenantMarketplace = {
  default_marketplace?: string;
  enabled_marketplaces?: string[];
  account_mappings?: Record<string, string>;
};

export type TenantWarehouse = {
  default_warehouse?: string;
  fulfillment_model?: string;
  warehouse_mappings?: Record<string, string>;
};

export type TenantContact = {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  role?: string;
  is_primary: boolean;
};

export type TenantAddress = {
  id: string;
  label?: string;
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  postal_code?: string;
  country: string;
  is_primary: boolean;
};

export type TenantPreferences = {
  notifications_enabled: boolean;
  dashboard_layout?: string;
  warehouse_defaults?: Record<string, string>;
  business_calendar?: Record<string, string>;
  marketplace_settings?: Record<string, string>;
  extra?: Record<string, string>;
};

export type Tenant = {
  id: string;
  organization_id: string;
  slug: string;
  name: string;
  status: TenantStatus;
  description?: string;
  metadata?: Record<string, string>;
  settings?: Record<string, string>;
  configuration?: Record<string, string>;
  branding?: TenantBranding;
  profile?: TenantProfile;
  business?: TenantBusiness;
  tax?: TenantTax;
  marketplace?: TenantMarketplace;
  warehouse?: TenantWarehouse;
  preferences?: TenantPreferences;
  tags?: string[];
  contacts?: TenantContact[];
  addresses?: TenantAddress[];
  created_at: string;
  updated_at: string;
  activated_at?: string;
  suspended_at?: string;
  archived_at?: string;
};

export type SettingsBundle = {
  settings: Record<string, string>;
  configuration: Record<string, string>;
  preferences: TenantPreferences;
};

export type ActivityEntry = {
  id: string;
  action: string;
  actor_id?: string;
  summary: string;
  metadata?: Record<string, unknown>;
  created_at: string;
};

export type AuditEntry = {
  id: string;
  action: string;
  actor_id?: string;
  outcome: string;
  resource?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
};

export type SwitchResult = {
  tenant_id: string;
  organization_id: string;
  slug: string;
  name: string;
  status: TenantStatus;
};

export type TenantDashboard = {
  tenant: Tenant;
  contact_count: number;
  address_count: number;
  recent_activity: ActivityEntry[];
  status: TenantStatus;
  organization_id: string;
};

export type CreateTenantInput = {
  organization_id?: string;
  name: string;
  slug?: string;
  description?: string;
  activate?: boolean;
  metadata?: Record<string, string>;
  profile?: TenantProfile;
  business?: TenantBusiness;
  tax?: TenantTax;
  marketplace?: TenantMarketplace;
  warehouse?: TenantWarehouse;
  branding?: TenantBranding;
  tags?: string[];
};

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function tenantFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Tenants ${path} failed: ${res.status} ${text}`);
  }
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const tenantApi = {
  list: (params?: { organization_id?: string; status?: string; q?: string }) => {
    const qs = new URLSearchParams();
    if (params?.organization_id) qs.set('organization_id', params.organization_id);
    if (params?.status) qs.set('status', params.status);
    if (params?.q) qs.set('q', params.q);
    const query = qs.toString();
    return tenantFetch<Tenant[]>(`/tenants${query ? `?${query}` : ''}`);
  },

  search: (params?: Record<string, string>) => {
    const qs = new URLSearchParams(params);
    return tenantFetch<{ results: Tenant[]; count: number }>(`/tenants/search?${qs.toString()}`);
  },

  get: (id: string) => tenantFetch<Tenant>(`/tenants/${id}`),

  create: (input: CreateTenantInput) =>
    tenantFetch<Tenant>('/tenants', { method: 'POST', body: JSON.stringify(input) }),

  update: (id: string, input: Partial<CreateTenantInput>) =>
    tenantFetch<Tenant>(`/tenants/${id}`, { method: 'PUT', body: JSON.stringify(input) }),

  patch: (id: string, input: Record<string, unknown>) =>
    tenantFetch<Tenant>(`/tenants/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),

  activate: (id: string) => tenantFetch<Tenant>(`/tenants/${id}/activate`, { method: 'POST' }),

  suspend: (id: string) => tenantFetch<Tenant>(`/tenants/${id}/suspend`, { method: 'POST' }),

  archive: (id: string) => tenantFetch<Tenant>(`/tenants/${id}/archive`, { method: 'POST' }),

  restore: (id: string) => tenantFetch<Tenant>(`/tenants/${id}/restore`, { method: 'POST' }),

  clone: (id: string, input: { name: string; slug?: string }) =>
    tenantFetch<Tenant>(`/tenants/${id}/clone`, { method: 'POST', body: JSON.stringify(input) }),

  switch: async (id: string): Promise<SwitchResult> => {
    const result = await tenantFetch<SwitchResult>(`/tenants/${id}/switch`, { method: 'POST' });
    setScope(result.tenant_id, result.organization_id);
    return result;
  },

  remove: (id: string) => tenantFetch<Tenant>(`/tenants/${id}`, { method: 'DELETE' }),

  getSettings: (id: string) => tenantFetch<SettingsBundle>(`/tenants/${id}/settings`),

  updateSettings: (id: string, input: Partial<SettingsBundle>) =>
    tenantFetch<SettingsBundle>(`/tenants/${id}/settings`, { method: 'PUT', body: JSON.stringify(input) }),

  getActivity: (id: string, limit = 50) =>
    tenantFetch<ActivityEntry[]>(`/tenants/${id}/activity?limit=${limit}`),

  getAudit: (id: string, limit = 50) =>
    tenantFetch<AuditEntry[]>(`/tenants/${id}/audit?limit=${limit}`),

  getDashboard: (id: string) => tenantFetch<TenantDashboard>(`/tenants/${id}/dashboard`),
};
